import { cn } from '@bridebook/aura';
import { cva, type VariantProps } from 'class-variance-authority';
import { HTMLAttributes } from 'react';

const roundTickVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center rounded-full',
  {
    variants: {
      size: {
        sm: 'size-[20px]',
        md: 'size-[30px]',
        lg: 'size-[40px]',
      },
      color: {
        dark: 'bg-space text-white',
        light: 'bg-white text-space',
      },
    },
    defaultVariants: {
      size: 'lg',
      color: 'dark',
    },
  },
);

export interface RoundTickProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'color'>,
    VariantProps<typeof roundTickVariants> {
  size?: NonNullable<VariantProps<typeof roundTickVariants>['size']>;
  color?: NonNullable<VariantProps<typeof roundTickVariants>['color']>;
}

export const RoundTick = ({ size = 'lg', color = 'dark', className, ...props }: RoundTickProps) => (
  <div className={cn(roundTickVariants({ size, color }), className)} {...props}>
    <TickSvg size={size} />
  </div>
);

const TickSvg = ({ size }: { size: 'sm' | 'md' | 'lg' }) => {
  switch (size) {
    case 'lg':
      return (
        <svg
          width={40}
          height={40}
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M11 20L16.625 27L29 13"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'md':
      return (
        <svg
          width={30}
          height={30}
          viewBox="0 0 30 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8.25 15L12.4688 20.25L21.75 9.75"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case 'sm':
      return (
        <svg
          width={20}
          height={20}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M5.5 10L8.3125 13.5L14.5 6.5"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
};
